/**
 * @fileoverview Kbd - Keyboard key display component
 *
 * Purpose: Display keyboard shortcuts and keys in documentation/UI
 * Dependencies: None
 * Used by: Tooltips, command menus, keyboard shortcuts display
 *
 * Components exported:
 * - Kbd: Individual key display
 * - KbdGroup: Group of keys (for combinations like ⌘K)
 *
 * Features:
 * - Consistent keyboard key styling
 * - Support for icons (⌘, Ctrl symbols)
 * - Group for key combinations
 * - Theme-aware
 *
 * @example
 * <Kbd>K</Kbd>
 *
 * @example
 * // Key combination
 * <KbdGroup>
 *   <Kbd>⌘</Kbd>
 *   <Kbd>K</Kbd>
 * </KbdGroup>
 */

import { cn } from "@/utils/misc";

function Kbd({ className, ...props }: React.ComponentProps<"kbd">) {
  return (
    <kbd
      data-slot="kbd"
      className={cn(
        "pointer-events-none inline-flex h-5 w-fit min-w-5 select-none items-center justify-center gap-1 rounded-sm bg-muted px-1 font-sans text-xs font-medium text-muted-foreground",
        "[&_svg:not([class*='size-'])]:size-3",
        "[[data-slot=tooltip-content]_&]:bg-background/20 [[data-slot=tooltip-content]_&]:text-background dark:[[data-slot=tooltip-content]_&]:bg-background/10",
        className,
      )}
      {...props}
    />
  );
}

function KbdGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <kbd
      data-slot="kbd-group"
      className={cn("inline-flex items-center gap-1", className)}
      {...props}
    />
  );
}

export { Kbd, KbdGroup };
